import { FormularioDoEmpenho } from './empenho';

/**
 * LER A NOTA DE EMPENHO DO PDF (19/10/2026, fase 3 do empenho, backend #494). `POST /contracts/:id/empenhos/ler-pdf`
 * (ADMIN, multipart `file`) lê o PDF e devolve os campos com a confiança de cada um. NÃO grava nada: a leitura só
 * PREENCHE o formulário, o cadastro continua no `POST :id/empenhos` que a pessoa confirma, e o PDF só sobe como anexo
 * depois de o empenho existir (`anexar`), como já era.
 *
 * ⛔ Campo que o PDF não traz (`null`) fica VAZIO e editável, nunca chutado: no SAMU a competência não está no PDF
 * ("para um periodo de 4 meses" não diz de quando a quando) e o backend avisa com `COMPETENCIA_NAO_ESTA_NO_PDF`.
 * ⛔ Recusa (emissor desconhecido, PDF de imagem, nota de outro credor, arquivo inválido) NÃO vira formulário vazio:
 * o formulário fica como estava e a tela diz a frase do código. Decisão sempre pelo CÓDIGO, nunca pela frase.
 */
export type ConfiancaDoCampo = 'alta' | 'media';
export type CampoLido<T> = { valor: T; confianca: ConfiancaDoCampo; origem?: string };
export type AvisoDaLeitura = { codigo: string; mensagem: string };
export type LeituraDoEmpenho = {
  ok: true;
  reconhecedor: string;
  numero: CampoLido<string> | null;
  emissao: CampoLido<string> | null;
  valor: CampoLido<number> | null;
  cnpjCredor: CampoLido<string> | null;
  numeroDoContrato: CampoLido<string> | null;
  competenciaInicio: CampoLido<string> | null;
  competenciaFim: CampoLido<string> | null;
  avisos: AvisoDaLeitura[];
};

/** Os campos do formulário que a leitura preenche, e de onde cada um sai na resposta. */
export const CAMPOS_LIDOS = {
  number: 'numero', issuedAt: 'emissao', totalValue: 'valor', competenciaInicio: 'competenciaInicio', competenciaFim: 'competenciaFim',
} as const;
export type CampoDoFormularioLido = keyof typeof CAMPOS_LIDOS;

/** Quem leu, com nome de gente; reconhecedor que a tela não conhece aparece como veio. */
export const ROTULO_DO_RECONHECEDOR: Record<string, string> = {
  'foz-do-iguacu': 'Foz do Iguaçu (SAMU)',
  'cro-mg-processos-net': 'CRO-MG (Processos.Net)',
};

/** As recusas da leitura, pelo código. Ler um PDF de imagem é diferente de não reconhecer o emissor. */
export const FRASE_DA_RECUSA: Record<string, string> = {
  PDF_SEM_TEXTO: 'Não consegui ler: o PDF é imagem (digitalização), sem texto. Isso não quer dizer que a nota não tenha os dados.',
  EMISSOR_NAO_RECONHECIDO: 'Emissor desconhecido: não reconheço o layout desta nota, e não adivinho campo de layout novo.',
  CREDOR_NAO_E_IPCOM: 'A nota não é da IPCOM: o credor é outro CNPJ. Confira se o arquivo certo foi escolhido.',
  EMPENHO_ANEXO_INVALIDO: 'O arquivo não serve:',
};

/** Os avisos da leitura, pelo código. Não bloqueiam; o tom diz o peso. */
export const ROTULO_DO_AVISO_DA_LEITURA: Record<string, string> = {
  COMPETENCIA_NAO_ESTA_NO_PDF: 'Competência não está no PDF',
  VALOR_NAO_FECHA_COM_SALDOS: 'O valor não fecha com os saldos',
  VALORES_DIVERGEM_NO_PDF: 'Valores divergem no PDF',
  CNPJ_NAO_LIDO: 'CNPJ do credor não lido',
  CONTRATO_DIVERGE: 'Contrato diferente',
  EMPENHO_JA_CADASTRADO: 'Empenho já cadastrado',
};
/** Cadastrar de novo criaria saldo em dobro: este vai em vermelho. Os demais em âmbar. */
export const tomDoAvisoDaLeitura = (codigo: string): 'danger' | 'warning' => (codigo === 'EMPENHO_JA_CADASTRADO' ? 'danger' : 'warning');

/** 29300 → "29.300,00": o formato que o campo de valor já aceita (`valorDigitado`). */
export function valorParaOCampo(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A leitura é a resposta de sucesso do backend? Qualquer outra forma é erro, nunca formulário vazio. */
export function ehLeitura(r: any): r is LeituraDoEmpenho {
  return !!r && typeof r === 'object' && r.ok === true && typeof r.reconhecedor === 'string';
}

/**
 * O formulário com o que voltou. Os cinco campos lidos são substituídos pelo PDF, e o que o PDF não trouxe fica VAZIO
 * (não o que estava antes, nem um valor derivado de outro campo). Escopo e observação não estão no PDF e ficam como
 * estavam.
 */
export function formularioDaLeitura(f: FormularioDoEmpenho, l: LeituraDoEmpenho): FormularioDoEmpenho {
  const v = l.valor && Number.isFinite(Number(l.valor.valor)) && Number(l.valor.valor) > 0 ? valorParaOCampo(Number(l.valor.valor)) : '';
  const mes = (c: CampoLido<string> | null) => (c && /^\d{4}-(0[1-9]|1[0-2])$/.test(c.valor) ? c.valor : '');
  return {
    ...f,
    number: l.numero?.valor?.trim() || '',
    issuedAt: l.emissao && /^\d{4}-\d{2}-\d{2}$/.test(l.emissao.valor) ? l.emissao.valor : '',
    totalValue: v,
    competenciaInicio: mes(l.competenciaInicio),
    competenciaFim: mes(l.competenciaFim),
  };
}

/** O selo ao lado do campo: a confiança enquanto o valor é o que o PDF deu; "alterado" quando a pessoa mudou ou preencheu. */
export type SeloDoCampo = { tipo: 'alta' | 'media' | 'ausente' | 'alterado'; origem?: string };
export function seloDoCampo(campo: CampoDoFormularioLido, l: LeituraDoEmpenho, lido: FormularioDoEmpenho, atual: FormularioDoEmpenho): SeloDoCampo {
  const c = l[CAMPOS_LIDOS[campo]] as CampoLido<unknown> | null;
  if (atual[campo] !== lido[campo]) return { tipo: 'alterado' };
  if (!c || !lido[campo]) return { tipo: 'ausente' };
  return c.confianca === 'alta' ? { tipo: 'alta' } : { tipo: 'media', origem: c.origem };
}

/** A frase de uma recusa (ou de uma falha qualquer) da leitura. Nunca vazia, nunca "não há dados". */
export function fraseDaFalhaDaLeitura(e: any): string {
  const d = e?.response?.data;
  const codigo = typeof d?.codigo === 'string' ? d.codigo : null;
  const msg = String(d?.mensagem || d?.message || e?.message || '').trim();
  if (codigo && FRASE_DA_RECUSA[codigo]) {
    return codigo === 'EMPENHO_ANEXO_INVALIDO' ? `${FRASE_DA_RECUSA[codigo]} ${msg || 'arquivo inválido.'}` : FRASE_DA_RECUSA[codigo];
  }
  return `Não consegui ler o PDF${msg ? ` (${msg})` : ''}.`;
}
